import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useOptiAssets } from '@/hooks/useOptiAssets';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, Upload, Trash2, Loader2, Download, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format, isPast, addDays, isBefore } from 'date-fns';

type DocType = 'warranty' | 'inspection' | 'certification' | 'receipt';

const DOC_TYPE_LABELS: Record<DocType, string> = {
  warranty: 'Warranty',
  inspection: 'Inspection',
  certification: 'Certification',
  receipt: 'Receipt',
};

interface AssetDocument {
  id: string;
  asset_id: string;
  storage_path: string;
  doc_type: DocType;
  title: string;
  expires_at: string | null;
  created_at: string;
  created_by: string | null;
}

interface AssetDocumentsTabProps {
  assetId: string;
}

export function AssetDocumentsTab({ assetId }: AssetDocumentsTabProps) {
  const { activeShop } = useAuth();
  const { canEdit } = useOptiAssets();
  const queryClient = useQueryClient();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [docType, setDocType] = useState<DocType>('receipt');
  const [title, setTitle] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['asset-documents', assetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shop_asset_documents')
        .select('*')
        .eq('asset_id', assetId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as AssetDocument[];
    },
    enabled: !!assetId,
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile || !activeShop || !title) throw new Error('Missing required fields');

      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${activeShop.id}/${assetId}/docs/${crypto.randomUUID()}.${fileExt}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('asset_files')
        .upload(fileName, selectedFile);
      if (uploadError) throw uploadError;

      // Create document record
      const { error: insertError } = await supabase
        .from('shop_asset_documents')
        .insert({
          asset_id: assetId,
          shop_id: activeShop.id,
          storage_path: fileName,
          doc_type: docType,
          title,
          expires_at: expiresAt || null,
        });
      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-documents', assetId] });
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setTitle('');
      setExpiresAt('');
      setDocType('receipt');
      toast.success('Document uploaded');
    },
    onError: (error) => {
      toast.error('Failed to upload document: ' + error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (doc: AssetDocument) => {
      // Delete from storage
      await supabase.storage.from('asset_files').remove([doc.storage_path]);
      // Delete record
      const { error } = await supabase
        .from('shop_asset_documents')
        .delete()
        .eq('id', doc.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-documents', assetId] });
      toast.success('Document deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete document: ' + error.message);
    },
  });

  const getDocUrl = async (storagePath: string) => {
    const { data, error } = await supabase.storage
      .from('asset_files')
      .createSignedUrl(storagePath, 3600);
    if (error) {
      toast.error('Failed to get document URL');
      return null;
    }
    return data.signedUrl;
  };

  const handleDownload = async (doc: AssetDocument) => {
    const url = await getDocUrl(doc.storage_path);
    if (url) {
      window.open(url, '_blank');
    }
  };

  const getExpiryStatus = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const date = new Date(expiresAt);
    if (isPast(date)) return 'expired';
    if (isBefore(date, addDays(new Date(), 30))) return 'expiring';
    return 'valid';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const expiredCount = documents.filter(d => getExpiryStatus(d.expires_at) === 'expired').length;
  const expiringCount = documents.filter(d => getExpiryStatus(d.expires_at) === 'expiring').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-muted-foreground">
            {documents.length} document{documents.length !== 1 ? 's' : ''}
          </h3>
          {expiredCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              {expiredCount} expired
            </Badge>
          )}
          {expiringCount > 0 && (
            <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-600">
              {expiringCount} expiring soon
            </Badge>
          )}
        </div>
        {canEdit && (
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <FileText className="h-4 w-4" />
                Add Document
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Document</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Document Type</Label>
                  <Select value={docType} onValueChange={(v) => setDocType(v as DocType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(DOC_TYPE_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Hunter Alignment Warranty"
                  />
                </div>
                <div className="space-y-2">
                  <Label>File</Label>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expires (optional)</Label>
                  <Input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                  />
                </div>
                <Button
                  onClick={() => uploadMutation.mutate()}
                  disabled={!selectedFile || !title || uploadMutation.isPending}
                  className="w-full"
                >
                  {uploadMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Upload
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {documents.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground">No documents uploaded yet</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => {
            const expiryStatus = getExpiryStatus(doc.expires_at);
            return (
              <Card key={doc.id} className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{doc.title}</span>
                      <Badge variant="secondary" className="text-xs">
                        {DOC_TYPE_LABELS[doc.doc_type]}
                      </Badge>
                      {expiryStatus === 'expired' && (
                        <Badge variant="destructive" className="text-xs gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Expired
                        </Badge>
                      )}
                      {expiryStatus === 'expiring' && (
                        <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-600 gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Expires soon
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Uploaded {format(new Date(doc.created_at), 'MMM d, yyyy')}
                      {doc.expires_at && (
                        <> · Expires {format(new Date(doc.expires_at), 'MMM d, yyyy')}</>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => handleDownload(doc)}>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteMutation.mutate(doc)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
